import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { axiosInstance } from "utils";
import { useIsMobile } from "hooks";
import { DeleteModal } from "@molecules";
import { Button, Typography } from "@atoms";
import CancelIcon from "@assets/icons/cancel.svg";

import { AddPeopleContent } from "./components";
import {
  AddPeopleContentButtons,
  AddPeopleContentTitle,
  Divider,
  MobileWrapper
} from "./addPeople.styles";
import { useStoryFormContext } from "contexts/StoryFormContext";
import { useStoriesDataContext } from "contexts/StoriesContext";
import { IStory } from "pages/People/people.types";
import { useStoryDataContext } from "contexts/StoryContext";

interface IAddPeople {
  isEdit?: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
}

function AddPeople({
  isEdit,
  open,
  setOpen,
}: IAddPeople) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addStory, removeStory, editStory, isMy} = useStoriesDataContext();
  const { story, setStory } = useStoryDataContext();
  const isMobile = useIsMobile(767);
  const { formData } = useStoryFormContext();
  const [isLoading, setIsLoading] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);

    try {
      const formPayload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "image" && value !== null && typeof value !== "string") formPayload.append(key, value);
        if (value !== null && key !== 'image') formPayload.append(key, String(value));
        if (value === null) formPayload.append(key, "null");
      });

      if (isEdit && story?.uuid) {
        const response = await axiosInstance.put<IStory>(`my-stories/${story.uuid}/`, formPayload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setStory(response.data);
        editStory(response.data);
      } else {
        const response = await axiosInstance.post<IStory>("my-stories/", formPayload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        addStory(response.data);
        navigate(`/${isMy ? 'my-stories' : 'stories'}/${response.data.uuid}`);
      }

      setOpen(false);
      toast.success(t("dataSave"), {
        position: "bottom-right",
        theme: "colored",
      });
    } catch (err) {
      // @ts-ignore
      toast.error(Object.values(err.response.data)[0][0], {
        position: "bottom-right",
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteStory = async () => {
    if (!story?.uuid) return;
    try {
      await axiosInstance.delete(`my-stories/${story.uuid}/`);
      removeStory(story.uuid);
      navigate(`/${isMy ? 'my-stories' : 'stories'}/`);
    } catch (err) {
      console.error("Ошибка при загрузке данных:", err);
    }
  };

  const isDisabled = isEdit ? false : !formData.name || !formData.status;
  const title = isEdit ? t("editStory") : t("addStory");

  return (
    isMobile && open ? (
      <MobileWrapper>
        <AddPeopleContentTitle>
          <Typography style={{color: 'rgba(57, 29, 10, 1)'}} as="h2">{title}</Typography>
          <img src={CancelIcon} alt="cancel" onClick={() => setOpen(false)} />
        </AddPeopleContentTitle>

        <AddPeopleContent
          handleDeleteStory={handleDeleteStory}
        />

        <AddPeopleContentButtons>
          {isEdit && <Button disabled={isLoading} onClick={() => setDeleteModalOpen(true)} variant="outlined">Удалить</Button>}
          <Button disabled={isLoading} onClick={handleClick}>Опубликовать</Button>
        </AddPeopleContentButtons>

        <DeleteModal
          title={t("deletePage")}
          text={t("approveDeletePage")}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onOk={handleDeleteStory}
        />
      </MobileWrapper>
    ) : (
      <Modal
        forceRender
        title={<div>{title}</div>}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleClick}
        cancelText={t("close")}
        okButtonProps={{ disabled: isDisabled || isLoading }}
        cancelButtonProps={{ disabled: isDisabled || isLoading }}
        okText={t("publish")}
        width="fit-content"
      >
        <Divider />
        <AddPeopleContent
          handleDeleteStory={handleDeleteStory}
        />
        <Divider />
      </Modal>
    )
  );
}

export default AddPeople;
